import * as Yup from "yup";
import { useState } from "react";
import { useFormik, Form, FormikProvider } from "formik";
import { useNavigate } from "react-router-dom";
// material
import { Stack, TextField, Link } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import LoadingButton from "@mui/lab/LoadingButton";
import axios from "../../../util/axios";
import token from "../../../util/Token";
import OTPVerification from "../register/OTPVerification";
import { useDomainConfig } from "../../../hooks/useDomainConfig";

// ----------------------------------------------------------------------

export default function ForgetPasswordForm() {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState();
  const [emailSent, setEmailSent] = useState(false);
  const [openOtpVerify, setOpenOtpVerify] = useState(false);
  // const [phoneForgotPasswordId, setPhoneForgotPasswordId] = useState(null);
  const [forgotPasswordId, setForgotPasswordId] = useState(null);
  const [resetPasswordPayloadObj, setResetPasswordPayloadObj] = useState({});
  const domainConfig = useDomainConfig();
  const phoneNoRegex = /^\d{10}$/i;
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/i;
  const [contactType, setContactType] = useState("");

  const forgetPasswordSchema = Yup.object().shape({
    // email: Yup.string().email('Email must be a valid email address').required('Email is required')
    contact: Yup.string()
      .test(
        "phone-or-email",
        "Please enter a valid phone number or email address",
        (value) => {
          if (phoneNoRegex.test(value) || emailRegex.test(value)) return true;
          return false;
        }
      )
      .required("Either email or phone no. is required"),
  });

  const formik = useFormik({
    initialValues: {
      contact: "",
    },
    validationSchema: forgetPasswordSchema,
    onSubmit: (resetPasswordData, { setSubmitting, resetForm }) => {
      (async function () {
        try {
          const registerHelper = async () => {
            let resetPayload = {
              type: emailRegex.test(resetPasswordData.contact)
                ? "email"
                : "phone",
            };
            resetPayload.domain = domainConfig.domain;
            if (resetPayload.type === "email")
              resetPayload.email = resetPasswordData.contact;
            else {
              resetPayload.phone = resetPasswordData.contact;
              resetPayload.countryCode = domainConfig.countryCode;
            }
            setResetPasswordPayloadObj(resetPayload);
            setContactType(resetPayload.type);
            const response = await axios(
              `${process.env.REACT_APP_API_END_POINT}auth/v2/forget-password`,
              {
                method: "post",
                data: resetPayload,
              }
            );
            if (response.data && response.data.success) {
              const forgetPasswordResponse = response.data.data;
              if (resetPayload.type === "phone" || resetPayload.type === "email") {
                setForgotPasswordId(forgetPasswordResponse.data);
                setEmailSent(true);
                setOpenOtpVerify(true);
              }
            } else {
              // Handle error responses directly from success=false
              setError(response.data?.message || "User detail not found");
              setEmailSent(false);
            }
            setSubmitting(false);
          };
          await registerHelper();
        } catch (error) {
          setSubmitting(false);
          setEmailSent(false);
          if (
            error.response &&
            error.response.data &&
            error.response.data.message
          ) {
            setError(error.response.data.message);
          } else {
            setError("An error occurred. Please try again.");
          }
        }
      })();
      //navigate('/dashboard', { replace: true });
    },
  });

  const resendOtpFunc = async () => {
    return new Promise(async (res, rej) => {
      try {
        const response = await axios(
          `${process.env.REACT_APP_API_END_POINT}auth/v2/forget-password`,
          {
            method: "post",
            data: resetPasswordPayloadObj,
          }
        );

        const registerHelperResp = response.data;
        if (registerHelperResp && registerHelperResp.success) {
          setForgotPasswordId(registerHelperResp.data.data);
          res("resend success");
        }
      } catch (error) {
        rej(error.response.message);
      }
    });
  };
  const submitOtp = (otp) => {
    return new Promise(async (res, rej) => {
      try {
        const otpPayload = {
          id: forgotPasswordId,
          domain: domainConfig.domain,
          otp: otp.Otp,
        };
        const url = `${process.env.REACT_APP_API_END_POINT}auth/verify-change-password-otp`;
        const response = await axios(url, {
          method: "post",
          data: otpPayload,
        });
        res(response.data);
      } catch (error) {
        rej(error.response.data.message);
      }
    });
  };

  const onSuccessfullVerify = (otp) => {
    resetForm();
    navigate(`/reset-password/${forgotPasswordId}/${otp}/${contactType}`);
  };

  const {
    errors,
    touched,
    handleSubmit,
    isSubmitting,
    getFieldProps,
    resetForm,
    values,
  } = formik;

  return (
    <FormikProvider value={formik}>
      <div style={{ color: "red", textAlign: 'center', marginBottom: '16px', fontSize: '14px' }}>{error}</div>
      <div style={{ color: "green", textAlign: 'center', marginBottom: '16px', fontSize: '14px' }}>{successMessage}</div>
      <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
        <Stack spacing={3}>
          <TextField
             fullWidth
             autoComplete="username"
             type="text"
             label="Email address / Phone number"
             {...getFieldProps("contact")}
             error={Boolean(touched.contact && errors.contact) || Boolean(error)}
             helperText={(touched.contact && errors.contact)}
             sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                fontSize: '1rem',
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'primary.main',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderWidth: 2,
                },
              },
              '& .MuiInputLabel-root': {
                fontSize: '0.95rem',
                fontWeight: 500,
              },
             }}
          />

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ my: 1 }}
          >
            <Link component={RouterLink} variant="subtitle2" to="/login" sx={{ color: 'primary.main', fontWeight: 600 }}>
              Back to login
            </Link>
          </Stack>
          
          <LoadingButton
            fullWidth
            size="large"
            type="submit"
            variant="contained"
            loading={isSubmitting}
            sx={{
              height: 52,
              fontSize: '1.1rem',
              fontWeight: 700,
              letterSpacing: '0.5px',
              textTransform: 'none',
              borderRadius: 3,
              background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
              boxShadow: '0 4px 14px 0 rgba(25, 118, 210, 0.39)',
              mt: 2,
              mb: 2,
              '&:hover': {
                background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)',
                boxShadow: '0 6px 20px 0 rgba(25, 118, 210, 0.5)',
                transform: 'translateY(-2px)',
              },
              '&:active': {
                transform: 'translateY(0)',
              },
              '&.Mui-disabled': {
                background: 'linear-gradient(135deg, #e0e0e0 0%, #f5f5f5 100%)',
                color: 'text.disabled',
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            Submit
          </LoadingButton>
        </Stack>
      </Form>
      {openOtpVerify && (
        <OTPVerification
          contactType={contactType}
          contact={values.contact}
          onClose={() => setOpenOtpVerify(false)}
          open={openOtpVerify}
          submitOtpFunc={submitOtp}
          handleSuccess={onSuccessfullVerify}
          countryCode={
            contactType === "phone" ? domainConfig.countryCode : undefined
          }
          resendOtp={resendOtpFunc}
          forgotPasswordId={forgotPasswordId}
        />
      )}
    </FormikProvider>
  );
}
